import { useMemo } from "react";
import keyBy from "lodash.keyby";
import { useTranslation } from "react-i18next";

import { type StripeIntegration, type TPayIntegration } from "@fitness-app/data-models/entities/ProductsSettings";

import { LIST_ITEM_SEPARATOR } from "~/constants/separators";
import { useAppSelector } from "~/store/initializeStore";

export const usePaymentGatewayOptions = () => {
  const { t } = useTranslation(["settings", "common"]);
  const { data } = useAppSelector((state) => state.productsSettings);

  const tpayOptions = useMemo(() => {
    if (!data?.paymentGateways?.TPAY) {
      return [];
    }

    return data.paymentGateways.TPAY.map((integration: TPayIntegration) => ({
      label: `${integration.name} -  ${integration.posId} (${
        integration.isDefault ? t("settings:default") : t("settings:notDefault")
      })`,
      value: `${integration.posId}${LIST_ITEM_SEPARATOR}${integration.isDefault ?? false}`,
      disabled: integration.isDefault,
      isDefault: integration.isDefault,
    }));
  }, [data?.paymentGateways]);

  const stripeOptions = useMemo(() => {
    if (!data?.paymentGateways?.STRIPE) {
      return [];
    }

    return data.paymentGateways.STRIPE.map((integration: StripeIntegration) => ({
      label: `${integration.name} - ${integration.accountId} - ${
        integration.isDefault ? t("settings:default") : t("settings:notDefault")
      }`,
      value: `${integration.accountId}${LIST_ITEM_SEPARATOR}${integration.isDefault ?? false}`,
      disabled: integration.isDefault,
      isDefault: integration.isDefault,
    }));
  }, [data?.paymentGateways]);

  const stripeOptionsByKey = useMemo(
    () => (data?.paymentGateways?.STRIPE ? keyBy(data?.paymentGateways?.STRIPE, "accountId") : {}),
    [data?.paymentGateways?.STRIPE],
  );
  const tpayOptionsByKey = useMemo(
    () => (data?.paymentGateways?.TPAY ? keyBy(data?.paymentGateways?.TPAY, "posId") : {}),
    [data?.paymentGateways?.TPAY],
  );

  return {
    tpayOptions,
    stripeOptions,
    stripeOptionsByKey,
    tpayOptionsByKey,
  };
};
